import React, { useState, useEffect, useMemo } from "react";
import { Modal, FormInput, FormTextarea } from "@/components/ui";
import { Send, Search } from "lucide-react";
import toast from "react-hot-toast";
import API from "@/lib/API";
import { ROLE_LABELS } from "@/constants/globalConstants";

export const AddMaterialExpenseModal = ({ isOpen, onClose, projects = [], onSuccess, user = null }) => {
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    relatedToProject: "yes",
    relatedProjectId: "",
    relatedProjectCollection: "",
    reason: "",
    amount: "",
    toWhomPaid: "",
    byWhomPaid: "",
    notes: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);

  useEffect(() => {
    if (isOpen) {
      let defaultByWhomPaid = "";
      if (user) {
        defaultByWhomPaid = (user.name || `${user.firstName || ""} ${user.lastName || ""}`).trim();
      }

      setForm({
        date: new Date().toISOString().split('T')[0],
        relatedToProject: "yes",
        relatedProjectId: "",
        relatedProjectCollection: "",
        reason: "",
        amount: "",
        toWhomPaid: "",
        byWhomPaid: defaultByWhomPaid,
        notes: "",
      });
      setProjectSearch("");
    }
  }, [isOpen, user]);

  const filteredProjects = useMemo(() => {
    if (!projectSearch) return projects;
    return projects.filter(p =>
      (p.customerCustomId || p.projectId || '').toLowerCase().includes(projectSearch.toLowerCase()) ||
      (p.customerName || '').toLowerCase().includes(projectSearch.toLowerCase())
    );
  }, [projects, projectSearch]);

  const handleProjectSelect = (p) => {
    setForm(prev => ({
      ...prev,
      relatedProjectId: p._id || p.projectId,
      relatedProjectCollection: p.model || 'VerveProjectExecution'
    }));
    setProjectSearch(`${p.customerCustomId || p.projectId} — ${p.customerName}`);
    setShowProjectDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || !form.reason || !form.toWhomPaid || !form.date) {
      toast.error("Please fill all required fields");
      return;
    }
    if (form.relatedToProject === "yes" && !form.relatedProjectId) {
      toast.error("Please select a project ID");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        date: form.date,
        relatedToProject: form.relatedToProject === "yes",
        relatedProjectId: form.relatedToProject === "yes" ? form.relatedProjectId : null,
        relatedProjectCollection: form.relatedToProject === "yes" ? (form.relatedProjectCollection || 'VerveProjectExecution') : null,
        reason: form.reason, // Maps to Material Name
        category: "MATERIAL",
        amount: Number(form.amount),
        toWhomPaid: form.toWhomPaid, // Maps to Supplier
        notes: form.notes,
        status: "pending",
      };

      await API.post("/general-expenses", payload);
      toast.success("Material expense requested successfully");
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save expense");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="REQUEST PROJECT EXPENSE" size="max-w-2xl">
      <div className="mb-4 text-[11px] font-medium text-slate-500">
        This form is used for expenses related to project, advance paid to customer.
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Row 1: Request by, request date */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormInput
            label="Request by *"
            value={((user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`).trim() || "User") + ` (${ROLE_LABELS[user?.role] || user?.role || "Staff Member"})`}
            readOnly
            required
          />
          <FormInput
            label="Request date *"
            type="date"
            value={form.date}
            onChange={e => setForm({ ...form, date: e.target.value })}
            required
          />
        </div>

        {/* Row 2: Amount, To whom */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormInput
            label="Amount *"
            type="number"
            placeholder="0.00"
            value={form.amount}
            onChange={e => setForm({ ...form, amount: e.target.value })}
            required
          />
          <FormInput
            label="To whom *"
            type="text"
            placeholder="e.g. Reliance Power"
            value={form.toWhomPaid}
            onChange={e => setForm({ ...form, toWhomPaid: e.target.value })}
            required
          />
        </div>

        {/* Row 3: Request for reason */}
        <FormInput
          label="Request for reason *"
          type="text"
          placeholder="e.g. Office Electricity Bill"
          value={form.reason}
          onChange={e => setForm({ ...form, reason: e.target.value })}
          required
        />

        {/* Row between reason and notes: Related to Project */}
        <div>
          <label className="flex items-center gap-1.5 text-[13px] font-medium text-slate-500 mb-1">Related to Project?</label>
          <div className="flex items-center gap-6 mt-1">
            <div className="flex items-center gap-4">
              {["yes", "no"].map(opt => (
                <label key={opt} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="relatedToProject"
                    value={opt}
                    checked={form.relatedToProject === opt}
                    onChange={e => {
                      const val = e.target.value;
                      let pid = form.relatedProjectId;
                      let coll = form.relatedProjectCollection;
                      if (val === "no") {
                        pid = "";
                        coll = "";
                        setProjectSearch("");
                      }
                      setForm({ ...form, relatedToProject: val, relatedProjectId: pid, relatedProjectCollection: coll });
                    }}
                    className="accent-brand-blue w-4 h-4"
                  />
                  <span className="text-[13px] font-medium text-slate-600 capitalize">{opt === "yes" ? "Yes" : "No"}</span>
                </label>
              ))}
            </div>
            {form.relatedToProject === "yes" && (
              <div className="flex-1 flex flex-row items-start gap-3">
                <div className="flex-1">
                <div className="relative group">
                  <FormInput
                    placeholder="Search Project / ID..."
                    value={projectSearch}
                    onChange={(e) => {
                      setProjectSearch(e.target.value);
                      setShowProjectDropdown(true);
                    }}
                    onFocus={() => setShowProjectDropdown(true)}
                    required={form.relatedToProject === "yes" && !form.relatedProjectId}
                    rightIcon={<Search size={14} />}
                  />

                  {showProjectDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-56 overflow-y-auto no-scrollbar py-2">
                      {filteredProjects.length > 0 ? (
                        filteredProjects.map((p, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className="w-full px-4 py-2.5 text-left hover:bg-blue-50/50 transition-colors flex flex-col gap-0.5 border-b border-slate-50 last:border-0"
                            onClick={() => handleProjectSelect(p)}
                          >
                            <span className="text-xs font-bold text-brand-dark">{p.customerName}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-[9px] font-black text-brand-blue bg-blue-50 px-1.5 py-0.5 rounded uppercase tracking-wider">
                                {p.customerCustomId || p.projectId}
                              </span>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-center text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                          No matching projects
                        </div>
                      )}
                    </div>
                  )}
                  {showProjectDropdown && <div className="fixed inset-0 z-40" onClick={() => setShowProjectDropdown(false)} />}
                </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Row 4: Notes */}
        <FormTextarea
          label="Notes"
          placeholder="Describe the expense detail..."
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          rows={3}
        />

        <div className="flex gap-3 mt-5 pt-4 border-t border-slate-100">
          <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button type="submit" disabled={submitting} className="btn-primary flex-1 flex items-center justify-center gap-2">
            {submitting ? (
              <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Send size={14} />
                Request for Approval
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};
